import { useCallback, useEffect, useRef, useState } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import type { VideoAsset } from '../../../services/admin/adminBackendServiceVideoLibraryMixin';
import { useAttachedAssetResync } from './useAttachedAssetResync';

const POLL_MS = 3000;

export function stillProcessing(status: VideoAsset['status']): boolean {
  return status === 'PENDING' || status === 'PROCESSING';
}

/**
 * Tracks one card's attached video asset and polls while it's PENDING/PROCESSING (interval
 * only exists during that window, cleared on READY/FAILED/unmount). Also resyncs from
 * `attachedExternalId` on mount/change, so a draft-restored card picks up where it left off.
 */
export function useCardVideoAssetPoll({
  backendService,
  attachedExternalId,
  onReady,
  onFailed,
  onProcessing,
}: {
  backendService: BackendService;
  attachedExternalId?: string;
  onReady: () => void;
  onFailed: (message: string) => void;
  onProcessing: () => void;
}) {
  const [asset, setAsset] = useState<VideoAsset | null>(null);
  const backendServiceRef = useRef(backendService);
  backendServiceRef.current = backendService;
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const applyAsset = useCallback(
    (next: VideoAsset) => {
      setAsset(next);
      if (next.status === 'READY') {
        stopPoll();
        onReady();
        return;
      }
      if (next.status === 'FAILED') {
        stopPoll();
        onFailed(next.errorMessage || 'Processing failed');
        return;
      }
      if (stillProcessing(next.status)) onProcessing();
    },
    [stopPoll, onReady, onFailed, onProcessing],
  );

  const startPoll = useCallback(
    (externalId: string) => {
      if (pollRef.current) return;
      pollRef.current = setInterval(() => {
        void backendServiceRef.current
          .getVideoAsset(externalId)
          .then(applyAsset)
          .catch(() => {
            /* keep polling through transient errors, same as the howto editor */
          });
      }, POLL_MS);
    },
    [applyAsset],
  );

  useEffect(() => () => stopPoll(), [stopPoll]);

  useAttachedAssetResync({
    attachedExternalId,
    backendServiceRef,
    applyAsset,
    startPoll,
    stopPoll,
    onFailed,
  });

  return { asset, applyAsset, startPoll, backendServiceRef };
}
